import React, { useEffect, useRef, useState } from 'react';
import { Button, Stack, Typography, Box, CircularProgress, Tooltip, Paper, Popover } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LayersIcon from '@mui/icons-material/Layers';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BorderOuterIcon from '@mui/icons-material/BorderOuter';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import esriConfig from '@arcgis/core/config';
import EsriMap from '@arcgis/core/Map';
import MapView from '@arcgis/core/views/MapView';
import Graphic from '@arcgis/core/Graphic';
import Polygon from '@arcgis/core/geometry/Polygon';
import Polyline from '@arcgis/core/geometry/Polyline';
import WebTileLayer from '@arcgis/core/layers/WebTileLayer';
import TimeSlider from '@arcgis/core/widgets/TimeSlider';
import TimeInterval from '@arcgis/core/time/TimeInterval';
import { getWaybackItems, WaybackItem } from '@esri/wayback-core';
import { PolygonData, PolygonGroup, BoundaryData } from '../../types/schema';
import { GroupingOption } from '../../types/enums';

const BASEMAP_SATELLITE = 'satellite';
const BASEMAP_STREET = 'osm';

const getNeeColor = (nee: number | undefined, minNee: number, maxNee: number, isSelected: boolean): [number, number, number, number] => {
  const alpha = isSelected ? 0.8 : 0.5; // More opaque selected, more transparent not
  if (typeof nee !== 'number' || isNaN(nee)) {
    return [200, 200, 200, alpha]; // Grey for no data
  }

  const intensity = nee >= 0
    ? (maxNee > 0 ? Math.sqrt(nee / maxNee) : 0)
    : (minNee < 0 ? Math.sqrt(nee / minNee) : 0);

  if (nee >= 0) {
    // Positive NEE is RED (carbon source)
    const r = 255;
    let g, b;
    if (isSelected) {
      // Darker, more saturated red for selected
      g = 182 - 182 * intensity; // 182 -> 0
      b = 193 - 193 * intensity; // 193 -> 0
    } else {
      // Much lighter red for non-selected
      g = 230 - 80 * intensity; // 230 -> 150
      b = 230 - 80 * intensity; // 230 -> 150
    }
    return [r, g, b, alpha];
  } else {
    // Negative NEE is GREEN (carbon sink)
    let r, g, b;
    if (isSelected) {
      // Vibrant green for selected
      const darkGreen = { r: 103, g: 215, b: 162 };
      const lightGreen = { r: 200, g: 240, b: 220 };
      r = lightGreen.r - (lightGreen.r - darkGreen.r) * intensity;
      g = lightGreen.g - (lightGreen.g - darkGreen.g) * intensity;
      b = lightGreen.b - (lightGreen.b - darkGreen.b) * intensity;
    } else {
      // Much lighter green for non-selected
      const darkGreen = { r: 170, g: 225, b: 200 };
      const lightGreen = { r: 225, g: 245, b: 235 };
      r = lightGreen.r - (lightGreen.r - darkGreen.r) * intensity;
      g = lightGreen.g - (lightGreen.g - darkGreen.g) * intensity;
      b = lightGreen.b - (lightGreen.b - darkGreen.b) * intensity;
    }
    return [r, g, b, alpha];
  }
};

type ArcGISMapProps = {
  polygons?: PolygonData[];
  selectedPolygonIds?: number[];
  groupingOption?: GroupingOption;
  polygonGroups?: PolygonGroup[];
  selectedGroupIds?: string[];
  groupedPolygonDetails?: PolygonData[];
  groupDetailsStatus?: 'idle' | 'loading' | 'succeeded' | 'failed';
  boundaries?: BoundaryData[];
  showBoundaries?: boolean;
  onToggleBoundaries?: () => void;
  onPolygonClick?: (polygonId: number) => void;
};

const ArcGISMap: React.FC<ArcGISMapProps> = ({
  polygons = [],
  selectedPolygonIds = [],
  groupingOption = GroupingOption.NONE,
  polygonGroups = [],
  selectedGroupIds = [],
  groupedPolygonDetails = [],
  groupDetailsStatus = 'idle',
  boundaries = [],
  showBoundaries = true,
  onToggleBoundaries,
  onPolygonClick,
}) => {
  const mapDiv = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<EsriMap | null>(null);
  const viewRef = useRef<MapView | null>(null);
  const tileLayerRef = useRef<WebTileLayer | null>(null);
  const timeSliderRef = useRef<TimeSlider | null>(null);
  const [currentBasemap, setCurrentBasemap] = useState<string>(BASEMAP_STREET);
  const [currentItem, setCurrentItem] = useState<WaybackItem | null>(null);
  const [waybackItems, setWaybackItems] = useState<WaybackItem[]>([]);
  const [calendarAnchorEl, setCalendarAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Define center of Germany
  // const GERMANY_CENTER = {
  //   lat: 51.1657,
  //   lng: 10.4515,
  // };

  useEffect(() => {
    // Prevent re-initialization
    if (mapRef.current || !mapDiv.current) return;

    const apiKey = import.meta.env.VITE_ARCGIS_API_KEY;
    if (apiKey) {
      esriConfig.apiKey = apiKey;
    }

    const centerLng = parseFloat(import.meta.env.VITE_DEFAULT_CENTER_LNG || '11.7');
    const centerLat = parseFloat(import.meta.env.VITE_DEFAULT_CENTER_LAT || '55.5');
    const zoom = parseInt(import.meta.env.VITE_DEFAULT_ZOOM || '6', 10);

    const map = new EsriMap({ basemap: BASEMAP_STREET });
    mapRef.current = map;

    const view = new MapView({
      container: mapDiv.current,
      map: map,
      center: [centerLng, centerLat],
      zoom,
    });
    viewRef.current = view;

    // No return cleanup function needed if we are not destroying on re-render.
    // The component unmount will trigger this if needed, but the primary cause
    // of the AbortError is the strict mode double-effect run.
  }, []);

  useEffect(() => {
    // Fetch the list of wayback snapshots using the official library
    const fetchWaybackItems = async () => {
      try {
        const items = await getWaybackItems();
        setWaybackItems(items);
        // Don't set initial date - timeline is off by default
      } catch (error) {
        console.error("Failed to fetch Wayback items:", error);
      }
    };

    fetchWaybackItems();
  }, []);

  // Initialize TimeSlider when wayback items are available (but don't add tile layer yet)
  useEffect(() => {
    if (!viewRef.current || waybackItems.length === 0) return;

    // Initialize the TimeSlider (hidden, used for internal tracking)
    const timeSlider = new TimeSlider({
      container: 'timeSliderContainer',
      view: viewRef.current,
    });
    timeSliderRef.current = timeSlider;

    const startDate = new Date('2014-01-01');
    const endDate = new Date();
    timeSlider.fullTimeExtent = { start: startDate, end: endDate };
    timeSlider.timeExtent = { start: endDate, end: endDate };
    timeSlider.stops = {
      interval: new TimeInterval({
        value: 1,
        unit: 'years',
      }),
    };

    // Configure the labels to display the year
    timeSlider.labelFormatFunction = (value) => {
      const date = Array.isArray(value) ? value[0] : value;
      return `${date.getFullYear()}`;
    };
    timeSlider.tickConfigs = [{
      mode: "position",
      values: [startDate.getTime(), endDate.getTime()],
      labelsVisible: true,
      labelFormatFunction: (value) => {
        return `${new Date(value).getFullYear()}`;
      }
    }];
  }, [waybackItems]);

  // Add/remove wayback tile layer based on selected date
  useEffect(() => {
    if (!mapRef.current || !viewRef.current) return;

    // If a date is selected, add the wayback tile layer
    if (selectedDate && currentItem) {
      // Remove existing tile layer if any
      if (tileLayerRef.current) {
        mapRef.current.remove(tileLayerRef.current);
      }

      // Add new tile layer for selected date
      const newTileLayer = new WebTileLayer({
        urlTemplate: currentItem.itemURL,
        title: currentItem.itemTitle,
      });

      mapRef.current.add(newTileLayer);
      tileLayerRef.current = newTileLayer;
    } else {
      // Remove tile layer if no date is selected
      if (tileLayerRef.current) {
        mapRef.current.remove(tileLayerRef.current);
        tileLayerRef.current = null;
      }
    }
  }, [selectedDate, currentItem]);

  useEffect(() => {
    if (!viewRef.current) return;

    const view = viewRef.current;
    view.graphics.removeAll();

    const isGroupingActive = groupingOption && groupingOption !== GroupingOption.NONE;

    // When grouping is active, use the detailed polygons fetched for the groups.
    const allPolygons = isGroupingActive ? groupedPolygonDetails : polygons;

    if (!allPolygons || allPolygons.length === 0) return;

    const neeValues = allPolygons.map(p => p.neeTotal).filter((nee): nee is number => typeof nee === 'number' && !isNaN(nee));
    const maxNee = neeValues.length > 0 ? Math.max(...neeValues) : 0;
    const minNee = neeValues.length > 0 ? Math.min(...neeValues) : 0;

    // Store all group indices for each polygon (polygons can be in multiple groups)
    const polygonToGroupMap = new Map<number, number[]>();
    if (isGroupingActive && selectedGroupIds && polygonGroups) {
      selectedGroupIds.forEach((groupId, index) => {
        const group = polygonGroups.find(g => g.id === groupId);
        if (group && group.polygonIds) {
          group.polygonIds.forEach(pId => {
            const existingIndices = polygonToGroupMap.get(pId) || [];
            polygonToGroupMap.set(pId, [...existingIndices, index + 1]);
          });
        }
      });
    }

    const graphics = allPolygons.map((p: PolygonData) => {
      if (!p.geometry) {
        console.log('No geometry for polygon:', p.id);
        return null;
      }

      // Process polygon geometry

      try {
        // Validate coordinates structure
        const coords = p.geometry.coordinates[0];
        if (!coords || coords.length < 4) {
          console.error('Invalid polygon: not enough coordinates', coords);
          return null;
        }

        // Check if polygon is closed (first and last points should be the same)
        const firstPoint = coords[0];
        const lastPoint = coords[coords.length - 1];
        if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
          console.warn('Polygon not properly closed, fixing...');
          coords.push([firstPoint[0], firstPoint[1]]);
        }

        // Validate coordinate ranges
        const validCoords = coords.every(coord =>
          coord.length === 2 &&
          typeof coord[0] === 'number' &&
          typeof coord[1] === 'number' &&
          coord[0] >= -180 && coord[0] <= 180 &&
          coord[1] >= -90 && coord[1] <= 90
        );

        if (!validCoords) {
          console.error('Invalid coordinates detected:', coords);
          return null;
        }

        console.log('Validated coordinates:', coords.length, 'points');

        // Create polygon manually with rings
        const polygon = new Polygon({
          rings: [coords],
          spatialReference: { wkid: 4326 }
        });

        const groupIndices = polygonToGroupMap.get(p.id);
        const isSelectedIndividually = selectedPolygonIds.includes(p.id);

        const isSelected = isGroupingActive ? !!groupIndices : isSelectedIndividually;
        const selectionNumber = isGroupingActive
          ? groupIndices && groupIndices.length > 0
            ? [...groupIndices].sort((a, b) => a - b).join(',')
            : undefined
          : (isSelectedIndividually ? selectedPolygonIds.indexOf(p.id) + 1 : undefined);

        const color = getNeeColor(p.neeTotal, minNee, maxNee, isSelected);

        const polygonGraphic = new Graphic({
          geometry: polygon,
          symbol: {
            type: 'simple-fill',
            color: color,
            outline: {
              color: isSelected ? [0, 0, 0, 1] : [100, 100, 100, 0.5],
              width: isSelected ? 2 : 1,
            },
          },
          attributes: {
            ...p,
            ...(isSelected && { selectionNumber }),
          },
        });

        // Only add labels for selected polygons
        if (isSelected && selectionNumber) {
          const labelGraphic = new Graphic({
            geometry: {
              type: 'point',
              x: polygon.extent.center.x,
              y: polygon.extent.center.y,
              spatialReference: { wkid: 4326 }
            },
            symbol: {
              type: 'text',
              text: typeof selectionNumber === 'string' ? selectionNumber : selectionNumber.toString(),
              color: [255, 255, 255],
              font: {
                size: 16,
                weight: 'bold',
              },
              haloColor: [0, 0, 0],
              haloSize: 2,
            },
            attributes: {
              polygonId: p.id,
              selectionNumber,
            },
          });
          return [polygonGraphic, labelGraphic];
        }

        return [polygonGraphic];
      } catch (error) {
        console.error('Error creating polygon from geometry:', error, p.geometry);
        return null;
      }
    }).filter(Boolean).flat() as Graphic[];

    view.graphics.addMany(graphics);

    if (showBoundaries) {
      const boundaryGraphicsMap = new Map<number, Graphic>();
      const boundaryLabels: Graphic[] = [];

      boundaries.forEach((boundary) => {
        if (!boundary.geometry) return;

        try {
          const boundaryPolygon = new Polygon({
            rings: (boundary.geometry as any).coordinates,
            spatialReference: { wkid: 4326 },
          });

          const boundaryGraphic = new Graphic({
            geometry: boundaryPolygon,
            symbol: {
              type: 'simple-fill',
              color: [0, 0, 0, 0], // Transparent fill
              outline: {
                color: [255, 140, 0, 1], // Orange color (#ff8c00)
                width: 1,
                style: 'dash',
              },
            },
            attributes: {
              name: boundary.name,
              id: boundary.id,
              isBoundary: true,
            },
          });

          boundaryGraphicsMap.set(boundary.id, boundaryGraphic);

          // Create label at centroid
          const centroid = boundaryPolygon.extent.center;
          const labelGraphic = new Graphic({
            geometry: {
              type: 'point',
              x: centroid.x,
              y: centroid.y,
              spatialReference: { wkid: 4326 }
            },
            symbol: {
              type: 'text',
              text: boundary.name,
              color: [255, 140, 0, 1], // Orange color matching boundary
              font: {
                size: 14,
                weight: 'bold',
              },
              haloColor: [255, 255, 255, 0.8], // White halo for visibility
              haloSize: 2,
            },
            attributes: {
              boundaryId: boundary.id,
            },
          });

          boundaryLabels.push(labelGraphic);
        } catch (error) {
          console.error('Error creating boundary graphic:', error, boundary.geometry);
        }
      });

      view.graphics.addMany([...boundaryGraphicsMap.values(), ...boundaryLabels]);

      // Create polyline graphics for edges only (for hover detection)
      const boundaryEdgeGraphics: Graphic[] = [];
      boundaries.forEach((boundary) => {
        if (!boundary.geometry) return;
        try {
          const boundaryPolyline = new Polyline({
            paths: (boundary.geometry as any).coordinates,
            spatialReference: { wkid: 4326 },
          });

          const edgeGraphic = new Graphic({
            geometry: boundaryPolyline,
            symbol: {
              type: 'simple-line',
              color: [0, 0, 0, 0], // Invisible - only for hit testing
              width: 3, // Wider hit area for easier detection
            },
            attributes: {
              name: boundary.name,
              id: boundary.id,
              isBoundaryEdge: true,
            },
          });

          boundaryEdgeGraphics.push(edgeGraphic);
        } catch (error) {
          console.error('Error creating boundary edge graphic:', error);
        }
      });

      // Add edge graphics for hover detection (they're invisible)
      view.graphics.addMany(boundaryEdgeGraphics);

      // Add hover effect that only works on boundary edges
      let hoveredBoundaryId: number | null = null;
      const highlightHandle = view.on('pointer-move', (event) => {
        view.hitTest(event).then((response) => {
          // Check if hit is on a boundary edge (polyline), not the polygon fill
          const boundaryEdgeHit = response.results.find(
            (result) => 'graphic' in result &&
            result.graphic?.attributes?.isBoundaryEdge &&
            result.graphic.attributes.id
          ) as any;

          if (boundaryEdgeHit && boundaryEdgeHit.graphic) {
            const boundaryId = boundaryEdgeHit.graphic.attributes.id;

            // Find the corresponding polygon graphic
            const boundaryHit = boundaryGraphicsMap.get(boundaryId);

            // Only highlight if it's a different boundary
            if (hoveredBoundaryId !== boundaryId && boundaryHit) {
              // Restore previous boundary if any
              if (hoveredBoundaryId !== null) {
                const prevGraphic = boundaryGraphicsMap.get(hoveredBoundaryId);
                if (prevGraphic) {
                  view.graphics.remove(prevGraphic);
                  const boundary = boundaries.find(b => b.id === hoveredBoundaryId);
                  if (boundary) {
                    const boundaryPolygon = new Polygon({
                      rings: (boundary.geometry as any).coordinates,
                      spatialReference: { wkid: 4326 },
                    });
                    const restoredGraphic = new Graphic({
                      geometry: boundaryPolygon,
                      symbol: {
                        type: 'simple-fill',
                        color: [0, 0, 0, 0],
                        outline: {
                          color: [255, 140, 0, 1], // Orange color
                          width: 1,
                          style: 'dash',
                        },
                      },
                      attributes: {
                        name: boundary.name,
                        id: boundary.id,
                        isBoundary: true,
                      },
                    });
                    view.graphics.add(restoredGraphic);
                    boundaryGraphicsMap.set(hoveredBoundaryId!, restoredGraphic);
                  }
                }
              }

              // Highlight current boundary edge
              const boundary = boundaries.find(b => b.id === boundaryId);
              if (boundary && boundaryHit) {
                view.graphics.remove(boundaryHit);

                const highlightedPolygon = new Polygon({
                  rings: (boundary.geometry as any).coordinates,
                  spatialReference: { wkid: 4326 },
                });

                const highlightedGraphic = new Graphic({
                  geometry: highlightedPolygon,
                  symbol: {
                    type: 'simple-fill',
                    color: [0, 0, 0, 0],
                    outline: {
                      color: [255, 140, 0, 1], // Orange color
                      width: 2,
                      style: 'solid',
                    },
                  },
                  attributes: {
                    name: boundary.name,
                    id: boundary.id,
                    isBoundary: true,
                  },
                });

                view.graphics.add(highlightedGraphic);
                boundaryGraphicsMap.set(boundaryId, highlightedGraphic);
                hoveredBoundaryId = boundaryId;
              }
            }
          } else {
            // Remove highlight when not hovering over boundary edge
            if (hoveredBoundaryId !== null) {
              const prevGraphic = boundaryGraphicsMap.get(hoveredBoundaryId);
              if (prevGraphic) {
                view.graphics.remove(prevGraphic);
                const boundary = boundaries.find(b => b.id === hoveredBoundaryId);
                if (boundary) {
                  const boundaryPolygon = new Polygon({
                    rings: (boundary.geometry as any).coordinates,
                    spatialReference: { wkid: 4326 },
                  });
                  const restoredGraphic = new Graphic({
                    geometry: boundaryPolygon,
                    symbol: {
                      type: 'simple-fill',
                      color: [0, 0, 0, 0],
                      outline: {
                        color: [255, 140, 0, 1], // Orange color
                        width: 1,
                        style: 'dash',
                      },
                    },
                    attributes: {
                      name: boundary.name,
                      id: boundary.id,
                      isBoundary: true,
                    },
                  });
                  view.graphics.add(restoredGraphic);
                  boundaryGraphicsMap.set(hoveredBoundaryId, restoredGraphic);
                }
              }
              hoveredBoundaryId = null;
            }
          }
        });
      });

      // Store handle for cleanup
      (view as any)._boundaryHoverHandle = highlightHandle;
    } else {
      // Clean up hover handler when boundaries are hidden
      if ((view as any)._boundaryHoverHandle) {
        (view as any)._boundaryHoverHandle.remove();
        (view as any)._boundaryHoverHandle = null;
      }
    }

    // Zoom to selected polygons if any, otherwise show all polygons
    const selectedGraphics = graphics.filter(g => {
      const selectionNumber = g.attributes?.selectionNumber;
      return selectionNumber !== undefined && selectionNumber !== null &&
        (typeof selectionNumber === 'number' ? selectionNumber > 0 : selectionNumber.length > 0);
    });
    if (selectedGraphics.length > 0) {
      view.goTo(selectedGraphics).catch(err => {
        if (err.name !== "AbortError") {
          console.error("Error zooming to selected graphics:", err);
        }
      });
    } else if (graphics.length > 0) {
      view.goTo(graphics).catch(err => {
        if (err.name !== "AbortError") {
          console.error("Error zooming to all graphics:", err);
        }
      });
    }
  }, [polygons, selectedPolygonIds, groupingOption, polygonGroups, selectedGroupIds, groupedPolygonDetails, showBoundaries, boundaries]);

  // Handle polygon clicks for selection
  useEffect(() => {
    if (!viewRef.current || !onPolygonClick) return;

    const view = viewRef.current;
    const isGroupingActive = groupingOption && groupingOption !== GroupingOption.NONE;

    // Only enable polygon clicking when grouping is not active
    if (isGroupingActive) return;

    const clickHandle = view.on('click', async (event) => {
      try {
        const hitTestResult = await view.hitTest(event);

        // Find the first polygon graphic (not boundary or label)
        const polygonHit = hitTestResult.results.find(
          (result) =>
            'graphic' in result &&
            result.graphic &&
            result.graphic.attributes &&
            result.graphic.attributes.id &&
            !result.graphic.attributes.isBoundary &&
            !result.graphic.attributes.isBoundaryEdge &&
            !result.graphic.attributes.polygonId // Exclude label graphics
        ) as any;

        if (polygonHit && polygonHit.graphic) {
          const polygonId = polygonHit.graphic.attributes.id;
          if (typeof polygonId === 'number') {
            onPolygonClick(polygonId);
          }
        }
      } catch (error) {
        // Silently ignore hit test errors (e.g., when clicking on empty space)
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Error handling polygon click:', error);
        }
      }
    });

    return () => {
      clickHandle.remove();
    };
  }, [onPolygonClick, groupingOption]);

  // Change cursor to pointer when hovering over polygons (selected or unselected)
  useEffect(() => {
    if (!viewRef.current) return;

    const view = viewRef.current;
    const isGroupingActive = groupingOption && groupingOption !== GroupingOption.NONE;

    // Only enable cursor change when grouping is not active
    if (isGroupingActive) {
      // Reset cursor to default when grouping is active
      if (view.container) {
        view.container.style.cursor = 'default';
      }
      return;
    }

    // Throttle hit tests to avoid performance issues during map navigation
    let throttleTimeout: number | null = null;
    let lastHitTestTime = 0;
    const THROTTLE_MS = 200; // Only hit test every 200ms max

    const performHitTest = async (event: __esri.ViewPointerMoveEvent) => {
      // Skip if view is interacting (panning, zooming, etc.)
      if (view.interacting) {
        return;
      }

      const now = Date.now();
      if (now - lastHitTestTime < THROTTLE_MS) {
        return;
      }
      lastHitTestTime = now;

      try {
        const hitTestResult = await view.hitTest(event);

        // Find the first polygon graphic (not boundary or label)
        const polygonHit = hitTestResult.results.find(
          (result) =>
            'graphic' in result &&
            result.graphic &&
            result.graphic.attributes &&
            result.graphic.attributes.id &&
            !result.graphic.attributes.isBoundary &&
            !result.graphic.attributes.isBoundaryEdge &&
            !result.graphic.attributes.polygonId // Exclude label graphics
        ) as any;

        if (polygonHit && polygonHit.graphic && view.container) {
          // Change cursor to pointer for any polygon (selected or unselected)
          view.container.style.cursor = 'pointer';
        } else if (view.container) {
          // Reset cursor when not over a polygon
          view.container.style.cursor = 'default';
        }
      } catch (error) {
        // Silently ignore hit test errors
        if (error instanceof Error && error.name !== 'AbortError' && view.container) {
          view.container.style.cursor = 'default';
        }
      }
    };

    const pointerMoveHandle = view.on('pointer-move', (event) => {
      // Clear any pending throttle timeout
      if (throttleTimeout !== null) {
        clearTimeout(throttleTimeout);
      }

      // Skip hit test if view is interacting
      if (view.interacting) {
        if (view.container) {
          view.container.style.cursor = 'default';
        }
        return;
      }

      // Throttle the hit test
      throttleTimeout = window.setTimeout(() => {
        performHitTest(event);
        throttleTimeout = null;
      }, THROTTLE_MS);
    });

    // Reset cursor when map interaction starts
    const interactionStartHandle = view.watch('interacting', (isInteracting) => {
      if (isInteracting && view.container) {
        view.container.style.cursor = 'default';
      }
    });

    return () => {
      pointerMoveHandle.remove();
      interactionStartHandle.remove();
      if (throttleTimeout !== null) {
        clearTimeout(throttleTimeout);
      }
      // Reset cursor on cleanup
      if (view.container) {
        view.container.style.cursor = 'default';
      }
    };
  }, [groupingOption]);

  // Update basemap when state changes - REMOVED to prevent race condition

  // Handle calendar popover
  const handleCalendarOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setCalendarAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    setCalendarAnchorEl(null);
  };

  const calendarOpen = Boolean(calendarAnchorEl);

  // Handle calendar popover toggle
  const handleToggleTimeline = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (calendarOpen) {
      handleCalendarClose();
    } else {
      handleCalendarOpen(event);
    }
  };

  const handleZoomIn = () => {
    if (viewRef.current && !viewRef.current.destroyed) {
      viewRef.current.zoom += 1;
    }
  };

  const handleZoomOut = () => {
    if (viewRef.current && !viewRef.current.destroyed) {
      viewRef.current.zoom -= 1;
    }
  };

  const handleCenter = () => {
    if (viewRef.current && !viewRef.current.destroyed) {
      // Use selected polygons if any are selected, otherwise use all polygons
      const polygonsToCenter = selectedPolygonIds.length > 0
        ? polygons.filter(p => selectedPolygonIds.includes(p.id))
        : polygons;

      const graphics = polygonsToCenter
        .map(p => {
          if (!p.geometry) return null;

          try {
            const coords = p.geometry.coordinates[0];
            const polygon = new Polygon({
              rings: [coords],
              spatialReference: { wkid: 4326 },
            });
            return new Graphic({ geometry: polygon });
          } catch {
            return null;
          }
        })
        .filter(Boolean) as Graphic[];

      if (graphics.length > 0) {
        viewRef.current.goTo(graphics);
      }
    }
  };

  const handleBasemapToggle = () => {
    // Use 'osm' (OpenStreetMap) as it's a reliable raster basemap
    const newBasemap = currentBasemap === BASEMAP_STREET ? BASEMAP_SATELLITE : BASEMAP_STREET;
    console.log('Toggling basemap from', currentBasemap, 'to', newBasemap);
    setCurrentBasemap(newBasemap);

    if (viewRef.current?.map && !viewRef.current.destroyed) {
      // Remove wayback layer completely when toggling basemap so user can see the difference
      if (tileLayerRef.current && mapRef.current) {
        console.log('Removing wayback layer to show basemap change');
        mapRef.current.remove(tileLayerRef.current);
        tileLayerRef.current = null;
        // Clear the selected date state so wayback doesn't auto-restore
        setSelectedDate(null);
        setCurrentItem(null);
      }

      // Update the basemap
      console.log('Setting basemap to:', newBasemap);
      viewRef.current.map.basemap = newBasemap;
    } else {
      console.warn('Map or view not available for basemap toggle');
    }
  };

  // Get available dates from wayback items
  const availableDates = React.useMemo(() => {
    return waybackItems.map(item => new Date(item.releaseDatetime));
  }, [waybackItems]);

  // Check if a date has data available
  const isDateAvailable = (date: Date | null): boolean => {
    if (!date) return false;
    return availableDates.some(availableDate => {
      const dateStr = date.toDateString();
      const availableDateStr = availableDate.toDateString();
      return dateStr === availableDateStr;
    });
  };

  // Find the closest wayback item for a given date
  const findWaybackItemForDate = (date: Date): WaybackItem | null => {
    if (waybackItems.length === 0) return null;

    // Find exact match first
    const exactMatch = waybackItems.find(item => {
      const itemDate = new Date(item.releaseDatetime);
      return itemDate.toDateString() === date.toDateString();
    });

    if (exactMatch) return exactMatch;

    // Find closest match
    let closestItem = waybackItems[0];
    let closestDiff = Math.abs(date.getTime() - new Date(closestItem.releaseDatetime).getTime());

    waybackItems.forEach(item => {
      const itemDate = new Date(item.releaseDatetime);
      const diff = Math.abs(date.getTime() - itemDate.getTime());
      if (diff < closestDiff) {
        closestDiff = diff;
        closestItem = item;
      }
    });

    return closestItem;
  };

  // Handle date selection from calendar
  const handleDateChange = (date: Date | null) => {
    if (!mapRef.current) return;

    // If date is cleared (null), remove wayback and return to default basemap
    if (!date) {
      setSelectedDate(null);
      setCurrentItem(null);

      // Remove wayback tile layer
      if (tileLayerRef.current) {
        mapRef.current.remove(tileLayerRef.current);
        tileLayerRef.current = null;
      }

      // Close calendar after clearing
      handleCalendarClose();
      return;
    }

    // Date selected - find matching wayback item and add tile layer
    const matchingItem = findWaybackItemForDate(date);
    if (matchingItem) {
      setCurrentItem(matchingItem);
      setSelectedDate(date);

      // Update the tile layer
      if (tileLayerRef.current) {
        mapRef.current.remove(tileLayerRef.current);
      }

      const newTileLayer = new WebTileLayer({
        urlTemplate: matchingItem.itemURL,
        title: matchingItem.itemTitle,
      });

      mapRef.current.add(newTileLayer);
      tileLayerRef.current = newTileLayer;

      // Close calendar after selection
      handleCalendarClose();
    }
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div
        ref={mapDiv}
        style={{ width: '100%', height: '100%' }}
        aria-label="ArcGIS Map"
      />
      {groupDetailsStatus === 'loading' && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10,
            borderRadius: '8px',
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <Stack
        direction="column"
        spacing={1}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          zIndex: 1,
        }}
      >
        <Tooltip title="Zoom In">
          <Button variant="contained" onClick={handleZoomIn} aria-label="Zoom in">
            <AddIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Zoom Out">
          <Button variant="contained" onClick={handleZoomOut} aria-label="Zoom out">
            <RemoveIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Center map on polygons">
          <Button variant="contained" onClick={handleCenter} aria-label="Center map">
            <MyLocationIcon />
          </Button>
        </Tooltip>
        <Button
          variant="contained"
          onClick={handleBasemapToggle}
          aria-label={`Switch to ${currentBasemap === BASEMAP_STREET ? BASEMAP_SATELLITE : BASEMAP_STREET} view`}
          title={`Change map layer\n[Current: ${currentBasemap === BASEMAP_STREET ? 'Street View]' : 'Satellite View]'}`}
        >
          <LayersIcon />
        </Button>
        <Tooltip title="Satellite image date">
          <Button
            variant="contained"
            onClick={handleToggleTimeline}
            aria-label="Select date"
            sx={{
              backgroundColor: calendarOpen ? undefined : 'rgba(0, 0, 0, 0.4)',
            }}
          >
            <CalendarTodayIcon />
          </Button>
        </Tooltip>
        <Popover
          open={calendarOpen}
          anchorEl={calendarAnchorEl}
          onClose={handleCalendarClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <StaticDatePicker
              value={selectedDate}
              onChange={handleDateChange}
              shouldDisableDate={(date) => !isDateAvailable(date)}
              minDate={availableDates.length > 0 ? new Date(Math.min(...availableDates.map(d => d.getTime()))) : undefined}
              maxDate={availableDates.length > 0 ? new Date(Math.max(...availableDates.map(d => d.getTime()))) : undefined}
              slotProps={{
                actionBar: {
                  actions: ['today', 'clear'],
                },
                day: {
                  sx: {
                    '&.MuiPickersDay-root.Mui-disabled': {
                      opacity: 0.3,
                    },
                    '&.MuiPickersDay-root:not(.Mui-disabled)': {
                      fontWeight: 'bold',
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Popover>
      {onToggleBoundaries && (
        <Tooltip title="Boundary">
          <Button variant="contained" onClick={onToggleBoundaries} color={showBoundaries ? 'primary' : 'inherit'}>
            <BorderOuterIcon />
          </Button>
        </Tooltip>
      )}
      </Stack>
      <div
        id="timeSliderContainer"
        style={{
          display: 'none', // Hide the default slider UI
        }}
      />
      {selectedDate && (
        <Paper
          sx={{
            position: 'absolute',
            bottom: 24,
            left: 24,
            zIndex: 1,
            padding: '8px 16px',
            borderRadius: 2,
            boxShadow: 3,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
            {selectedDate.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </Typography>
        </Paper>
      )}
      <Box
        sx={{
          position: 'absolute',
          bottom: 8,
          right: 8,
          zIndex: 1,
          padding: '4px 8px',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Source: ArcGIS
        </Typography>
      </Box>
    </div>
  );
};

export default ArcGISMap;
